import type { ReactElement } from 'react'
import { BaseCurrencyPanel } from '~/components/BaseCurrency'
import { EntitiesPanel } from '~/components/Entities'
import { FiscalYearEndPanel } from '~/components/FiscalYearEnd'
import { OwnershipPanel } from '~/components/Ownership'
import { PrivateHoldingsPanel } from '~/components/PrivateHoldings'
import { TaxRatesPanel } from '~/components/TaxRates'
import { TimezonePanel } from '~/components/Timezone'
import { WarrantTaxRatesPanel } from '~/components/WarrantTaxRates'
import { chartCss } from '~/charts/theme'

/**
 * Three of these eight cards withhold themselves from a household they have
 * nothing to say to, so what this page holds depends on who is reading it.
 *
 * A household with nothing connected keeps the currency its figures are counted
 * in, the day its tax year closes, the zone those days are cut at and the list
 * of its own people and companies — the choices that are true of everybody — and
 * is not asked about extraction tax, an ownership chain or a funding round it
 * does not have. The entity list is deliberately not among the gated: every
 * other card here is reached by adding to it.
 */
export function Settings(): ReactElement {
  return (
    <main className="nw nw-settings">
      <style>{chartCss}</style>
      <h1>Settings</h1>
      <p className="nw-sub">Choices that change what every other figure means.</p>
      {/* First because it is the only card here that three other pages send
          people to by name, and the only one whose absence they announce. */}
      <TaxRatesPanel />
      {/* The other rates, beside the ones they are read against. A household
          looking for what its figures were charged at should find every answer
          in one place, and the warrant page links here for its own. */}
      <WarrantTaxRatesPanel />
      {/* Straight after the rates because it is what the threshold on that card
          is counted in, and the card above says so in words. */}
      <BaseCurrencyPanel />
      {/* Beside the currency because it is the other answer every household has
          whether or not it has connected anything, and because the two together
          are what a year-end pin is read under. */}
      <FiscalYearEndPanel />
      {/* Directly after the closing day, because the two are one question asked
          twice: which day a record is filed under, and where that day begins. */}
      <TimezonePanel />
      <EntitiesPanel />
      <OwnershipPanel />
      <PrivateHoldingsPanel />
    </main>
  )
}
